import type { InputHTMLAttributes, ReactElement, ReactNode } from 'react';

import { cn } from '../lib/cn';

/** 라벨·인풋·에러를 한 덩어리로 묶는다. 폼 화면이 많아 매번 다시 짜지 않는다. */
export function Field({
  id,
  label,
  error,
  hint,
  trailing,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  /** 칸 안 오른쪽 끝에 두는 버튼 자리 (예: 비밀번호 보기). 두면 글자가 그 밑으로 들어가지 않게 비운다. */
  trailing?: ReactNode;
}): ReactElement {
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[12.5px] font-medium text-muted-foreground">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(
            'h-11 w-full rounded-md border border-border bg-card px-3 text-sm',
            'focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary',
            error ? 'border-destructive' : '',
            trailing ? 'pr-11' : '',
            className,
          )}
          {...props}
        />
        {trailing ? (
          <div className="absolute inset-y-0 right-0 flex items-center pr-1.5">{trailing}</div>
        ) : null}
      </div>
      {hint ? (
        <span id={`${id}-hint`} className="text-[12px] text-muted-foreground">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-error`} className="text-[12px] text-destructive">
          {error}
        </span>
      ) : null}
    </div>
  );
}
